import React, { useState, useMemo } from 'react';
import Header from '../../components/ui/Header';
import ContentFilterBar from './components/ContentFilterBar';
import SortControls from './components/SortControls';
import ExpansionOverviewStats from './components/ExpansionOverviewStats';
import ProgressionGrid from './components/ProgressionGrid';

const ExpansionProgression = () => {
  const [filters, setFilters] = useState({
    search: '',
    contentType: '',
    completionStatus: ''
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: 'completion',
    sortOrder: 'desc'
  });

  const isOfficer = true;

  const contentAreas = [
    { id: 'blacksail', name: 'Blacksail Folly', icon: 'Ship', type: 'dungeon' },
    { id: 'maidens', name: "Maiden\'s Grave", icon: 'Skull', type: 'dungeon' },
    { id: 'katta', name: 'Katta Castrum', icon: 'Castle', type: 'zone' },
    { id: 'zhisza', name: "Zhisza\'s Lair", icon: 'Flame', type: 'raid' },
    { id: 'solteris', name: 'Solteris', icon: 'Crown', type: 'raid' },
    { id: 'sunderock', name: 'Sunderock Springs', icon: 'Mountain', type: 'zone' }
  ];

  const mockCharacters = [
    {
      id: 1,
      name: 'Thaldrin',
      class: 'Warrior',
      level: 60,
      completionPercentage: 85,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'completed',
        katta: 'completed',
        zhisza: 'in-progress',
        solteris: 'in-progress',
        sunderock: 'not-started'
      }
    },
    {
      id: 2,
      name: 'Mystara',
      class: 'Cleric',
      level: 58,
      completionPercentage: 72,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'completed',
        katta: 'in-progress',
        zhisza: 'in-progress',
        solteris: 'not-started',
        sunderock: 'not-started'
      }
    },
    {
      id: 3,
      name: 'Shadowbane',
      class: 'Rogue',
      level: 59,
      completionPercentage: 68,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'completed',
        katta: 'completed',
        zhisza: 'not-started',
        solteris: 'not-started',
        sunderock: 'in-progress'
      }
    },
    {
      id: 4,
      name: 'Firewing',
      class: 'Wizard',
      level: 57,
      completionPercentage: 55,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'in-progress',
        katta: 'in-progress',
        zhisza: 'not-started',
        solteris: 'not-started',
        sunderock: 'not-started'
      }
    },
    {
      id: 5,
      name: 'Ironforge',
      class: 'Paladin',
      level: 60,
      completionPercentage: 92,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'completed',
        katta: 'completed',
        zhisza: 'completed',
        solteris: 'in-progress',
        sunderock: 'completed'
      }
    },
    {
      id: 6,
      name: 'Lunara',
      class: 'Druid',
      level: 58,
      completionPercentage: 78,
      contentProgress: {
        blacksail: 'completed',
        maidens: 'completed',
        katta: 'completed',
        zhisza: 'in-progress',
        solteris: 'not-started',
        sunderock: 'in-progress'
      }
    }
  ];

  const contentTypeOptions = [
    { value: 'dungeon', label: 'Dungeons' },
    { value: 'zone', label: 'Zones' },
    { value: 'raid', label: 'Raids' }
  ];

  const completionStatusOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'not-started', label: 'Not Started' }
  ];

  const overviewStats = {
    overallCompletion: 75,
    activeCharacters: 6,
    totalCharacters: 8,
    contentAreas: 6,
    completedAreas: 4,
    trendingContent: 'Solteris'
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      contentType: '',
      completionStatus: ''
    });
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setSortConfig({ sortBy, sortOrder });
  };

  const handleStatusUpdate = (characterId, contentId, newStatus, notes) => {
    console.log('Status updated:', { characterId, contentId, newStatus, notes });
  };

  const filteredAndSortedCharacters = useMemo(() => {
    let result = [...mockCharacters];

    if (filters?.search) {
      result = result?.filter(char => 
        char?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.completionStatus) {
      result = result?.filter(char => {
        const hasStatus = Object.values(char?.contentProgress)?.some(
          status => status === filters?.completionStatus
        );
        return hasStatus;
      });
    }

    result?.sort((a, b) => {
      let comparison = 0;
      
      switch (sortConfig?.sortBy) {
        case 'completion':
          comparison = a?.completionPercentage - b?.completionPercentage;
          break;
        case 'name':
          comparison = a?.name?.localeCompare(b?.name);
          break;
        case 'content':
          const aCompleted = Object.values(a?.contentProgress)?.filter(s => s === 'completed')?.length;
          const bCompleted = Object.values(b?.contentProgress)?.filter(s => s === 'completed')?.length;
          comparison = aCompleted - bCompleted;
          break;
        default:
          comparison = 0;
      }

      return sortConfig?.sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [mockCharacters, filters, sortConfig]);

  const filteredContentAreas = useMemo(() => {
    if (!filters?.contentType) return contentAreas;
    return contentAreas?.filter(content => content?.type === filters?.contentType);
  }, [contentAreas, filters?.contentType]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
              Buried Sea Expansion Progression
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Track guild member completion across all Buried Sea content areas
            </p>
          </div>

          <ExpansionOverviewStats stats={overviewStats} />

          <ContentFilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            contentTypes={contentTypeOptions}
            completionStatuses={completionStatusOptions}
          />

          <SortControls
            sortBy={sortConfig?.sortBy}
            sortOrder={sortConfig?.sortOrder}
            onSortChange={handleSortChange}
          />

          <ProgressionGrid
            characters={filteredAndSortedCharacters}
            contentAreas={filteredContentAreas}
            isOfficer={isOfficer}
            onStatusUpdate={handleStatusUpdate}
          />

          {filteredAndSortedCharacters?.length === 0 && (
            <div className="bg-card rounded-xl border border-border p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No characters found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your filters to see more results</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExpansionProgression;